<template>
  <div v-if="visible" :class="[`modal-mask`, visible ? `modal-mask--visible` : '']">
    <section v-click-outside="(e) => onClickOutside(e)"
             :class="[
               `modal`,
               size === `medium` && `modal--medium`,
               size === `small` && `modal--small`,
             ]"
    >
      <header class="modal__header">
        <!-- @slot Header of the modal -->
        <slot name="header">Modal Header</slot>
        <div class="modal__header__close" data-toggle="modal-close" @click="close()">
          <icon class-name="modal__header__close__icon" name="times" />
        </div>
      </header>
      <article class="modal__content" :class="tertiaryButtonLabel || primaryButtonLabel ? 'modal__content--padding' : ''">
        <!-- @slot Main content of the modal -->
        <slot name="content">Default Modal Content (Fallback)</slot>
      </article>
      <footer
        v-if="tertiaryButtonLabel || primaryButtonLabel"
        class="modal__footer"
        :class="[linkButtonLabel && `modal__footer--justified`]"
      >
        <a
          v-if="linkButtonLabel"
          class="modal__footer__link"
          @click="linkButtonClick()"
        >{{ linkButtonLabel }}</a>
        <div 
          class="modal__footer__buttons" 
          :class="!deleteButtonLabel ? 'modal__footer__buttons--flex-end' : ''"
        >
          <div 
            v-if="deleteButtonLabel"
            :class="['modal__footer__buttons__left', { 'modal__footer__buttons__left--disabled': deleteButtonDisabled }]"
            @click="onDelete()"
          >
            <icon v-if="showDeleteButtonIcon" name="trash" />
            <span class="modal__footer__buttons__left--underline">{{ deleteButtonLabel }}</span>
          </div>
          <div class="modal__footer__buttons__right">
            <mds-button 
              v-if="tertiaryButtonLabel"
              appearance="tertiary"
              custom-class="button--tertiary--cancel"
              :label="tertiaryButtonLabel"
              :icon="tertiaryButtonIcon"
              :loading="tertiaryButtonLoading"
              :disabled="tertiaryButtonDisabled"
              :small="size === 'small'"
              @onClick="close()"
            />
            <mds-button
              v-if="primaryButtonLabel"
              appearance="primary"
              custom-class="button--primary--confirm"
              :label="primaryButtonLabel"
              :icon="primaryButtonIcon"
              :loading="primaryButtonLoading"
              :disabled="primaryButtonDisabled"
              :small="size === 'small'"
              @onClick="confirm()"
            />
          </div>
        </div>
      </footer>
      <slot name="footer"></slot>
    </section>
  </div>
</template>

<script>
// Components
import Icon from "../Icon/Icon";
import MdsButton from "../Button/Button";
import ClickOutside from "vue-click-outside";

export default {
  name: "Modal",
  directives: {
    ClickOutside,
  },
  components: {
    Icon,
    "mds-button": MdsButton,
  },
  props: {
    size: {
      type: String,
      validator: (val) => ["medium", "small", "fullscreen"].includes(val),
      default: "medium",
    },
    tertiaryButtonLabel: {
      type: String,
      default: "",
    },
    tertiaryButtonIcon: {
      type: String,
      default: "",
    },
    primaryButtonLabel: {
      type: String,
      default: "",
    },
    deleteButtonLabel: {
      type: String,
      default: "",
    },
    linkButtonLabel: {
      type: String,
      default: "",
    },
    primaryButtonIcon: {
      type: String,
      default: "",
    },
    active: {
      type: Boolean,
      default: false,
    },
    showDeleteButtonIcon: {
      type: Boolean,
      default: false,
    },
    deleteButtonDisabled: {
      type: Boolean,
      default: false,
    },
    tertiaryButtonLoading: {
      type: Boolean,
      default: false,
    },
    tertiaryButtonDisabled: {
      type: Boolean,
      default: false,
    },
    primaryButtonLoading: {
      type: Boolean,
      default: false,
    },
    primaryButtonDisabled: {
      type: Boolean,
      default: false,
    },
    closeOnClickOutside: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      visible: this.active,
    }
  },
  mounted() {
    if(this.visible) {
      document.documentElement.style.overflow = 'hidden';
    }
  },
  destroyed() {
    document.documentElement.style.overflow = 'unset';
  },
  methods: {
    close() {
      this.$emit("onClose");
    },
    confirm() {
      this.$emit("onConfirm");
    },
    onDelete() {
      this.$emit("onDelete");
    },
    linkButtonClick() {
      this.$emit("onLinkClick");
    },
    onClickOutside(e) {
      if(this.closeOnClickOutside && e.target.classList.contains('modal-mask')) {
        this.close();
      }
    }
  },
};
</script>

<style lang="scss" scoped>
.modal-mask{
  position: fixed;
  z-index: 999;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(20, 20, 20, 0.5);
  transition: all 0.5s ease-in-out;
  opacity: 0;
  &--visible {
    display: flex;
    justify-content: center;
    align-items: flex-end;
    opacity: 1;

    @media (min-width: 768px) {
      align-items: center;
    }
  }
}
  .modal {
    background: var(--Gray-50);
    overflow: hidden;
    transition: all 0.5s ease-in-out;
    $self: &;
    position: relative;
    &__header {
      font-size: 16px;
      line-height: 24px;
      letter-spacing: 0;
      padding: 24px;
      font-weight: 700;
      display: flex;
      justify-content: space-between;
      i {
        font-size: 24px;
        color: var(--Gray-600);
        cursor: pointer;
      }
    }
    &__content {
      padding: 0 24px 24px;
      overflow: auto;
      height: calc(100% - 92px);
      margin-right: 1px;
      &--padding {
        padding: 0 24px 60px;
      }
      &::-webkit-scrollbar {
        width: 8px;
        height: 5px;
      }
      /* Track */
      &::-webkit-scrollbar-track {
        border-radius: 10px;
        background: #f1f1f1;
      }
      /* Handle */
      &::-webkit-scrollbar-thumb {
        background: #888;
        border-radius: 10px;
      }
      /* Handle on hover */
      &::-webkit-scrollbar-thumb:hover {
        background: #555;
      }
    }
    &__footer {
      display: flex;
      justify-content: space-between;
      align-items: center;
      height: 4.5rem;
      position: sticky;
      bottom: 0;
      padding: 16px 24px;
      background-color: var(--Gray-100);
      &__link {
        cursor: pointer;
        color: var(--Blue-600-Dark);
      }
      &__buttons {
        display: flex;
        justify-content: space-between;
        width: 100%;
        &--flex-end {
          justify-content: flex-end;
        }
        &__left {
          display: flex;
          align-items: center;
          color: var(--Danger);
          &--disabled {
            cursor: not-allowed;
          }
          &--underline {
            text-decoration: underline;
            cursor: pointer;
          }
        }
        &__right {
          display: flex;
          align-items: center;
          a {
            margin-left: 16px;
          }
        }
      }
    }
    &--small {
      width: 400px;
      height: 225px;
      #{ $self }__footer {
        height: 4rem;
      }
    }
    &--medium {
      height: 800px;
      width: 400px;
    }
    &-page__modal__window__content{
      margin-right: 1px;
      &::-webkit-scrollbar {
        width: 8px;
        height: 5px;
      }
      /* Track */
      &::-webkit-scrollbar-track {
        border-radius: 10px;
        background: #f1f1f1;
      }
      /* Handle */
      &::-webkit-scrollbar-thumb {
        background: #888;
        border-radius: 10px;
      }
      /* Handle on hover */
      &::-webkit-scrollbar-thumb:hover {
        background: #555;
      }
    }
  }
</style>